<script lang="ts" module>
	import type { ISbRichtext, ISbStoryData, SbBlokData } from '@storyblok/js';

	export interface ProductDetails extends SbBlokData {
		component: 'product_details';
		name: string;
		description?: string;
		cart_fine_print?: ISbRichtext;
		stripe_product?: StripeProductData;
		product_image?: SbImage;
		icon: SbImage;
		price: string;
		treat_price_as_label?: boolean;
		content: Array<ISbStoryData>;
		custom_cart_cta?: string;
		thumbnail_badge_label?: string;
		thumbnail_image?: SbImage;
		min_weeks_shipping?: string;
		max_weeks_shipping?: string;
		financing_options?: FinancingOption;
		custom_order_success_page?: ISbStoryData;
	}
</script>

<script lang="ts">
	import { editable } from '../../editable';
	import type { FinancingOption, SbImage, StripeProductData } from '$lib/storyblok/types';
	import Blok from '$lib/storyblok/blok.svelte';
	import { checkoutSession } from '$lib/state/checkoutSession.svelte';
	import { tick } from 'svelte';
	import { trackEvent } from '$lib/segment/client';

	interface Props {
		content: ISbStoryData<ProductDetails>['content'];
	}

	let { content }: Props = $props();

	let selected = $derived(
		content.stripe_product &&
			content.stripe_product.priceLookupKey === checkoutSession.data.product?.price.lookup_key
	);

	let height = $state(0);
	let contentWrapper = $state<HTMLDivElement | null>(null);

	async function setHeight() {
		if (selected && contentWrapper) {
			await tick();
			height = contentWrapper.scrollHeight;
		}
	}

	$effect.pre(() => {
		setHeight();
	});
</script>

<article
	use:editable={content}
	class={[
		'overflow-hidden rounded-xl bg-white text-[16px]/[150%] tracking-[-.16px] text-brown-1 lg:text-[20px]/[140%] lg:tracking-[-.2px]',
		{
			'outline outline-2 outline-brown-1': selected,
			'outline outline-1 outline-brown-5 hover:outline-2': !selected
		}
	]}
>
	<button
		type="button"
		class={[
			'w-full px-4 text-left transition-padding',
			{ 'py-[14px]': !selected, 'pb-3 pt-[14px]': selected }
		]}
		onclick={() => {
			if (content.stripe_product) {
				checkoutSession.select(content.stripe_product);
				trackEvent('Cart Product Selected', {
					product_name: content.name,
					price_lookup_key: content.stripe_product.priceLookupKey
				});
			}
		}}
		title="Select plan"
	>
		<div class="flex items-center justify-between gap-x-1">
			<div class="flex items-center gap-x-2">
				{#if content.icon?.filename?.length > 0}
					<img
						src={content.icon.filename}
						alt={content.icon.alt}
						title={content.icon.title}
						class="h-6 w-6 select-none"
					/>
				{/if}
				<h1>{content.name}</h1>
			</div>
			{#if content.price}
				{#if content.treat_price_as_label}
					<p
						class="rounded-[4px] bg-orange-6 px-3 py-2 text-[12px]/[100%] font-semibold uppercase text-orange-2"
					>
						{content.price}
					</p>
				{:else}
					<p>{content.price}</p>
				{/if}
			{/if}
		</div>

		{#if !selected && content.description && content.description.length > 0}
			<p class="text-[14px]/[160%] text-brown-4">
				{content.description}
			</p>
		{/if}

		{#if content.content?.length > 0}
			<div
				bind:this={contentWrapper}
				class="content-wrapper overflow-hidden transition-height duration-300 ease-out"
				style:height="{selected ? height : 0}px"
			>
				{#each content.content as block}
					<Blok content={block} />
				{/each}
			</div>
		{/if}
	</button>
</article>
